import { inject, observer } from 'mobx-react';
import React from 'react';

import AppLayoutReduced from 'components/AppLayout/AppLayoutReduced';
import ErrorMessage from 'components/ErrorMessage';
import Columns from 'components/Layout/Columns';
import Loading from 'components/Loading';
import SessionIcon from 'components/SessionIcon';
import { ApplicationStoreType } from 'models/ApplicationStore';
import { DataStoreType } from 'models/DataStore';
import { FireConfigStoreType } from 'models/FireConfigStore';
import { FireModelType, fireTypeOptions } from 'models/FireModel';
import { FireStoreType } from 'models/FireStore';
import { OrganizationsStoreType } from 'models/OrganizationsStore';
import { UserFireConfigStoreType } from 'models/UserFireConfigStore';
import {
  FormattedMessage,
  WrappedComponentProps,
  injectIntl
} from 'react-intl';
import GapSizes from 'utils/constants/gap-sizes';
import ROLES from 'utils/constants/roles';
import { ROUTE_FIRE, ROUTE_LAB } from 'utils/constants/routes';
import { HistoryProps, idFromMatch } from 'utils/history';
import useForm, { FormType } from 'utils/hooks/useForm';
import FireHeaderComponent from './components/FireHeaderComponent';
import FireInput from './components/FireInput';
import FireSidebarComponent from './components/FireSidebarComponent';
import FiresList from './components/FiresList';
import UserFireConfigBenchmarkSettings from './components/UserFireConfigBenchmarkSettings';

interface FireScreenProps {
  fireStore: FireStoreType;
  fireConfigStore: FireConfigStoreType;
  organizationsStore: OrganizationsStoreType;
  userFireConfigStore: UserFireConfigStoreType;
  dataStore: DataStoreType;
  applicationStore: ApplicationStoreType;
  form: FormType;
}

interface FireScreenOverlayState {
  showSettingsOverlay: boolean;
}

@inject(
  'fireStore',
  'fireConfigStore',
  'organizationsStore',
  'userFireConfigStore',
  'dataStore',
  'applicationStore'
)
@observer
class FireScreen extends React.Component<
  FireScreenProps & HistoryProps & WrappedComponentProps
> {
  state: FireScreenOverlayState = {
    showSettingsOverlay: false
  };

  componentDidMount() {
    const { dataStore } = this.props;
    const { user } = dataStore;
    if (!user || dataStore.isOrgViewerButNotEditor) {
      this.props.history.push(ROUTE_LAB);
    }

    this.props.form.reset();
    this.loadData();
  }

  componentDidUpdate(
    prevProps: FireScreenProps & HistoryProps & WrappedComponentProps
  ) {
    const prevOrgId = idFromMatch(prevProps.match, 'orgId');
    const currentOrgId = idFromMatch(this.props.match, 'orgId');

    if (prevOrgId !== currentOrgId) {
      this.loadData();
    }
  }

  loadData = async () => {
    await this.loadOrganization();
    await this.loadCustomers();
    await this.loadFires();
    await this.loadFireConfigs();
    await this.loadUserFireConfig();
  };

  loadOrganization = async () => {
    const { organizationsStore, dataStore } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');

    if (!organizationId) {
      return;
    }

    if (dataStore.organization?.id === organizationId) {
      // organization has been loaded already
      return;
    }

    const orgFromStore = dataStore.organizations.get(organizationId.toString());
    if (orgFromStore) {
      // TODO We might refresh organization info at this point in the future. For now organizations
      // do not carry any information that is expected to change often so we can rely on cached data.
      dataStore.setOrganization(orgFromStore);
      return;
    }

    const org = await organizationsStore.getOrganization(organizationId);

    // we need to select this org for the current context manually
    // TODO create organizationsStore.getOrganizationForContext() to handle all the context org related stuff?
    if (org) {
      dataStore.setOrganization(org);
    }
  };

  loadCustomers = async () => {
    const { organizationsStore, dataStore } = this.props;
    const { currentOrganization } = dataStore;

    if (!currentOrganization) {
      return;
    }

    if (currentOrganization.is_agency) {
      await organizationsStore.getCustomers();
    } else {
      const agency_id = currentOrganization.agency_id;
      if (agency_id) {
        await organizationsStore.getCustomers(agency_id);
      }
    }
  };

  loadFires = async () => {
    const { fireStore } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');

    if (!organizationId) {
      return;
    }

    await fireStore.getFires(organizationId);
  };

  loadFireConfigs = async () => {
    const { fireConfigStore, form } = this.props;

    await fireConfigStore.getFireConfigs();

    form.setField('fire_type', fireTypeOptions[0]);
    const defaultConfig = fireConfigStore.fireConfigList.find(
      (config) => config.default_config
    );
    form.setField(
      'fire_config_id',
      defaultConfig?.id || fireConfigStore.fireConfigList[0]?.id
    );
  };

  loadUserFireConfig = async () => {
    const { userFireConfigStore, form } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');

    if (!organizationId) {
      return;
    }

    await userFireConfigStore.getUserFireConfig(organizationId);

    form.setField('service', userFireConfigStore.userFireConfig?.service);
    form.setField(
      'target_group',
      userFireConfigStore.userFireConfig?.target_group
    );
    form.setField(
      'business_target',
      userFireConfigStore.userFireConfig?.business_target
    );
  };

  save = async () => {
    const { form, history, fireStore, applicationStore } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');

    if (!organizationId) {
      return;
    }

    form.setLoading(true);

    const mappedFormValues = {
      ...form.values,
      service_description: form.values.service,
      target_group_description: form.values.target_group,
      business_target_description: form.values.business_target
    };

    if (
      form.values.service === '' ||
      form.values.target_group === '' ||
      form.values.business_target === ''
    ) {
      applicationStore.setFlashMessage(
        this.props.intl.formatMessage({
          id: 'fire create empty settings flash'
        }),
        'error'
      );
      form.setLoading(false);
    }

    try {
      await fireStore.createFire(mappedFormValues, organizationId);
      history.push(
        ROUTE_FIRE + '/' + organizationId + '/' + fireStore.fire!.id
      );
    } catch (error) {
      applicationStore.setFlashMessage(
        this.props.intl.formatMessage({ id: 'fire create error flash' }),
        'error'
      );
      form.setLoading(false);
    }
  };

  deleteFire = async (fire: FireModelType) => {
    const { fireStore, form } = this.props;

    if (!fire.id) {
      return;
    }

    form.setLoading(true);

    if (
      !window.confirm(
        this.props.intl.formatMessage(
          { id: 'fire delete confirm' },
          { name: fire.project_name }
        )
      )
    ) {
      return;
    }

    try {
      await fireStore.deleteFire(fire.id);
      form.setLoading(false);
    } catch (error) {
      console.error('Failed to delete fire:', error);
      form.setLoading(false);
    }
  };

  handleServiceSubmit = async () => {
    const { userFireConfigStore, form } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');

    if (!organizationId) {
      return;
    }

    form.setLoading(true);
    await userFireConfigStore.updateUserFireConfig(organizationId, {
      service: form.values.service
    });

    form.setLoading(false);
  };

  handleTargetGroupSubmit = async () => {
    const { userFireConfigStore, form } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    if (!organizationId) {
      return;
    }

    form.setLoading(true);
    await userFireConfigStore.updateUserFireConfig(organizationId, {
      target_group: form.values.target_group
    });

    form.setLoading(false);
  };

  handleBusinessTargetSubmit = async () => {
    const { userFireConfigStore, form } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    if (!organizationId) {
      return;
    }

    form.setLoading(true);
    await userFireConfigStore.updateUserFireConfig(organizationId, {
      business_target: form.values.business_target
    });

    form.setLoading(false);
  };

  switchOrganization = (organizationId: string) => {
    this.props.history.push(ROUTE_FIRE + '/' + organizationId);
  };

  renderPage(content: React.ReactNode, showSidebar: boolean = true) {
    const { userFireConfigStore, dataStore } = this.props;
    const userFireConfig = userFireConfigStore.userFireConfig;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    const orgTargetGroups = dataStore.organization?.targetGroupList;
    const orgServices = dataStore.organization?.serviceList;

    return (
      <AppLayoutReduced active="fire">
        <Columns columnTemplate="1fr 22.1875rem" gap={GapSizes.NONE}>
          <div className="fire-layout-content">{content}</div>

          {showSidebar && (
            <FireSidebarComponent
              handleTargetGroupSubmit={this.handleTargetGroupSubmit}
              handleBusinessTargetSubmit={this.handleBusinessTargetSubmit}
              handleServiceSubmit={this.handleServiceSubmit}
              userFireConfig={userFireConfig!}
              form={this.props.form}
              organizationId={organizationId!}
              targetGroups={orgTargetGroups || []}
              services={orgServices || []}
              editable={true}
            />
          )}
        </Columns>
      </AppLayoutReduced>
    );
  }

  renderLoading() {
    return this.renderPage(<Loading />, false);
  }

  renderError() {
    return this.renderPage(
      <>
        <ErrorMessage
          state={this.props.fireStore.listLoadingState}
          onRetry={() => this.loadFires()}
        />
      </>,
      false
    );
  }

  render() {
    const {
      fireStore,
      fireConfigStore,
      organizationsStore,
      dataStore,
      form,
      userFireConfigStore
    } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    const { user, customers, currentOrganization } = dataStore;

    if (
      fireStore.isListLoading ||
      form.loading ||
      fireConfigStore.isListLoading ||
      organizationsStore.isItemLoading ||
      userFireConfigStore.isItemLoading ||
      !currentOrganization
    ) {
      return this.renderLoading();
    }

    if (
      fireStore.isListError ||
      fireConfigStore.isListError ||
      organizationsStore.isItemLoadError ||
      userFireConfigStore.isItemLoadError
    ) {
      return this.renderError();
    }

    if (!user || !organizationId || !user.org_memberships) {
      return null;
    }

    const organizations: { id: number; name: string }[] = [];

    // QUICK FIX: Customer Workshop
    if (user.role === ROLES.ADMIN) {
      organizations.push(...user.sortedOrganizationList);
    } else {
      const customerList = Array.from(customers.values()).map((customer) => ({
        id: customer.id,
        name: customer.name || ''
      }));

      organizations.push(...customerList);

      if (!currentOrganization.is_agency) {
        const agency_id = currentOrganization.agency_id;
        if (agency_id) {
          organizations.push({
            id: agency_id,
            name: currentOrganization.agency_name || ''
          });
        } else {
          organizations.push({
            id: currentOrganization.id,
            name: currentOrganization.name || ''
          });
        }
      } else {
        organizations.push({
          id: currentOrganization.id,
          name: currentOrganization.name || ''
        });
      }
    }

    return this.renderPage(
      <div className="l-item-stack-regular">
        <FireHeaderComponent
          currentOrganizationId={organizationId}
          organizations={organizations}
          isDetailView={false}
          switchOrganization={this.switchOrganization}
          setShowSettings={() => this.setState({ showSettingsOverlay: true })}
          editable={true}
        />

        <div className="fire-layout-content__overview-container">
          <div className="fire-intro-header">
            <div className="fire-intro-header__title">
              <SessionIcon width="2.5em" />

              <h2>
                <FormattedMessage
                  id="Fire intro header"
                  values={{
                    name: user.first_name
                  }}
                />
              </h2>
            </div>

            <div className="fire-intro-header__description">
              <FormattedMessage id="Fire intro description" />
            </div>
          </div>

          <FireInput
            form={form}
            fireConfigs={fireConfigStore.fireConfigList}
            handleNewFire={this.save}
          />

          <FiresList
            fires={dataStore.fireList || []}
            organizationId={organizationId}
            handleDeleteFire={this.deleteFire}
          />

          {this.state.showSettingsOverlay && (
            <UserFireConfigBenchmarkSettings
              organizationId={dataStore.currentOrganizationId!}
              userFireConfig={this.props.userFireConfigStore.userFireConfig}
              userFireConfigStore={this.props.userFireConfigStore}
              setShowSettings={() =>
                this.setState({ showSettingsOverlay: false })
              }
              onSubmit={() => this.setState({ showSettingsOverlay: false })}
              onAbort={() => this.setState({ showSettingsOverlay: false })}
            />
          )}
        </div>
      </div>
    );
  }
}

export default injectIntl((props: FireScreenProps & WrappedComponentProps) => {
  const form = useForm();
  return (
    // @ts-ignore
    <FireScreen {...props} form={form} />
  );
});
